import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { PasswordInput, Field } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import { api, errMsg, dashboardPath } from "@/lib/api";

export const AuthShell = ({ title, sub, children, testid }) => (
  <div className="mx-auto max-w-md px-4 py-10 sm:py-16" data-testid={testid}>
    <div className="rounded-3xl bg-white border border-slate-200 p-6 sm:p-8 shadow-sm">
      <h1 className="text-3xl font-extrabold tracking-tight text-[#0B192C]">{title}</h1>
      {sub && <p className="mt-2 text-sm text-slate-500">{sub}</p>}
      <div className="mt-7">{children}</div>
    </div>
  </div>
);

export const GoogleButton = () => {
  const { t } = useLang();
  const go = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button type="button" onClick={go} className="flex h-12 w-full items-center justify-center gap-3 rounded-full border border-slate-300 bg-white font-semibold text-[#142B5C] hover:bg-slate-50 transition-colors" data-testid="google-login-btn">
      <svg viewBox="0 0 48 48" className="h-5 w-5"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
      {t("google")}
    </button>
  );
};

export const Divider = () => {
  const { t } = useLang();
  return <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-widest text-slate-400"><span className="h-px flex-1 bg-slate-200" />{t("or")}<span className="h-px flex-1 bg-slate-200" /></div>;
};

export default function Login() {
  const { t } = useLang();
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/login", f);
      setUser(data);
      nav(sp.get("next") || dashboardPath(data));
    } catch (e2) {
      setErr(errMsg(e2));
    } finally { setBusy(false); }
  };
  return (
    <AuthShell title={t("login_title")} sub={t("login_sub")} testid="login-page">
      <GoogleButton />
      <Divider />
      <form onSubmit={submit} className="grid gap-4">
        <Field label={t("email")}><Input type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="h-12 rounded-xl" data-testid="login-email-input" /></Field>
        <Field label={t("password")}><PasswordInput required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} testid="login-password-input" /></Field>
        <Link to="/forgot-password" className="justify-self-end text-sm font-semibold text-[#FF6B00]" data-testid="forgot-password-link">{t("forgot")}</Link>
        {err && <p className="text-sm text-red-600" data-testid="login-error">{err}</p>}
        <button disabled={busy} className="h-12 rounded-full bg-[#FF6B00] font-bold text-white hover:bg-[#E05A00] disabled:opacity-60" data-testid="login-submit-btn">{t("login_btn")}</button>
      </form>
      <p className="mt-6 text-center text-sm text-slate-600">{t("no_account")} <Link to="/register" className="font-bold text-[#142B5C]" data-testid="go-register-link">{t("nav_register")}</Link></p>
    </AuthShell>
  );
}
